import axios from "axios";
import { useMemo, useCallback } from "react";
import { refreshTokenEndpoint } from "../services/constants";
import { ApiDataResponse, AppUser } from "../services/Dtos";
import { useUserContext } from "../Contexts/userContext";
import useToaster from "../hooks/useToaster";
import { useGlobalError } from "../Contexts/GlobalErrorContext";

// Utility to centralize logout logic
function logoutUser(setUser: (user: AppUser | null) => void) {
  setUser(null);
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const useAxios = () => {
  const { setUser } = useUserContext();
  const { showErrorToast } = useToaster();
  const { setError: setGlobalError } = useGlobalError();

  // Memoize the logout function to prevent unnecessary recreations
  const logoutUserCallback = useCallback(() => {
    logoutUser(setUser);
  }, [setUser]);

  // Memoize the error toast function
  const showErrorToastCallback = useCallback(
    (message: string) => {
      showErrorToast(message);
    },
    [showErrorToast]
  );

  // Memoize the global error setter
  const setGlobalErrorCallback = useCallback(
    (error: any) => {
      setGlobalError(error);
    },
    [setGlobalError]
  );

  const axiosClient = useMemo(() => {
    const client = axios.create({
      baseURL:
        import.meta.env.MODE === "development"
          ? import.meta.env.VITE_LOCAL_BASE_URL
          : import.meta.env.VITE_BASE_URL,
      withCredentials: true,
      timeout: 30000,
    });

    client.interceptors.request.use((config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { config: originalRequest, code: errorCode, response } = error;

        // 1. Handle critical network and timeout errors first
        if (
          errorCode === "ECONNABORTED" ||
          errorCode === "ERR_NETWORK" ||
          !response
        ) {
          const message =
            errorCode === "ECONNABORTED"
              ? "The request took too long. Please try again."
              : "Unable to connect to the server. Please check your connection or try again later.";

          setGlobalErrorCallback({
            message,
            onRetry: () => window.location.reload(),
          });
          return Promise.reject(error);
        }

        // 2. Skip refresh logic for login/register endpoints
        if (
          originalRequest.url?.includes("login") ||
          originalRequest.url?.includes("register")
        ) {
          return Promise.reject(error);
        }

        if (response.status === 401) {
          logoutUserCallback();
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          return new Promise((resolve, reject) => {
            client
              .post<ApiDataResponse<AppUser>>(refreshTokenEndpoint)
              .then(({ data }) => {
                if (!data.succeeded) throw new Error("Refresh failed");
                const token = data.data.token;
                localStorage.setItem("token", token);
                localStorage.setItem("user", JSON.stringify(data.data));
                client.defaults.headers.common["Authorization"] =
                  `Bearer ${token}`;
                processQueue(null, token);
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                resolve(client(originalRequest));
              })
              .catch((err) => {
                processQueue(err, null);
                logoutUserCallback();
                showErrorToastCallback("Session expired. Please log in again.");
                reject(err);
              })
              .finally(() => {
                isRefreshing = false;
              });
          });
        }

        // 4. Handle 5xx Server Errors
        if (response.status >= 500) {
          setGlobalErrorCallback({
            message:
              response.data?.message ||
              "A server error occurred. Please try again later.",
            onRetry: () => window.location.reload(),
          });
        }
        // 5. Handle 429 Too Many Requests specifically
        else if (response.status === 429) {
         
        }
        // 6. Handle other client errors with a toast if a message is available
        else if (response.data?.message) {
          //showErrorToastCallback(response.data.message);
        }

        return Promise.reject(error);
      }
    );

    return client;
  }, [logoutUserCallback, showErrorToastCallback, setGlobalErrorCallback]); // Remove toast from dependencies

  return axiosClient;
};

export default useAxios;
